import { useDebounceFn } from '@vueuse/core';
import { computed, ref } from 'vue';
import { reasonOf } from '../../../shared/api/client.ts';
import { changePhaseLengths, changeRounds, type PhaseLengthsChange } from '../../../shared/api/game.ts';

export const SETTINGS_SAVED_AFTER_MS = 2_000;

export type SettingsChange = { rounds: number } | PhaseLengthsChange;

export type Setting = 'chooserTime' | 'guessersTime' | 'rounds';

export const settingsSaveFailures = ref<Partial<Record<Setting, string>>>({});

export const settingsInSight = ref(false);

export const settingsSaveError = computed(() =>
  settingsInSight.value ? '' : (Object.values(settingsSaveFailures.value)[0] ?? ''),
);

const settingsOf = (change: SettingsChange) => Object.keys(change) as Setting[];

const failed = (settings: Setting[], reason: string) => {
  settingsSaveFailures.value = {
    ...settingsSaveFailures.value,
    ...Object.fromEntries(settings.map((setting) => [setting, reason])),
  };
};

const cleared = (settings: Setting[]) => {
  settingsSaveFailures.value = Object.fromEntries(
    Object.entries(settingsSaveFailures.value).filter(([setting]) => !settings.includes(setting as Setting)),
  );
};

const save = async (change: SettingsChange, saving: () => Promise<void>, onSaved: () => void) => {
  try {
    await saving();
  } catch (failure) {
    failed(settingsOf(change), reasonOf(failure));
    return;
  }

  cleared(settingsOf(change));
  onSaved();
};

const saveRounds = useDebounceFn(
  (rounds: number, onSaved: () => void) => save({ rounds }, () => changeRounds(rounds), onSaved),
  SETTINGS_SAVED_AFTER_MS,
);

let pendingLengths: PhaseLengthsChange = {};

const saveLengths = useDebounceFn((onSaved: () => void) => {
  const lengths = pendingLengths;
  pendingLengths = {};
  return save(lengths, () => changePhaseLengths(lengths), onSaved);
}, SETTINGS_SAVED_AFTER_MS);

export const changeSettings = (settingChanged: SettingsChange, onSaved: () => void) => {
  cleared(settingsOf(settingChanged));

  if ('rounds' in settingChanged) {
    saveRounds(settingChanged.rounds, onSaved);
    return;
  }

  pendingLengths = { ...pendingLengths, ...settingChanged };
  saveLengths(onSaved);
};
